// frontend/src/contexts/AuthContext.tsx - FINAL FIXED
import React, { createContext, useState, useContext, useEffect } from 'react'
import api, { User, SignUpData, ApiError, VerificationRequiredError } from '../utils/api'
import toast from 'react-hot-toast'

const TOKEN_KEYS = ['accessToken', 'token', 'authToken'] as const
const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

const getStoredToken = (): string | null => {
  for (const key of TOKEN_KEYS) {
    const token = localStorage.getItem(key)
    if (token) return token
  }
  return null
}

const setStoredToken = (token: string): void => {
  localStorage.setItem('accessToken', token)
  localStorage.setItem('token', token)
  localStorage.setItem('authToken', token)
}

const removeStoredToken = (): void => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('token')
  localStorage.removeItem('authToken')
  localStorage.removeItem('refreshToken')
}

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (data: SignUpData) => Promise<void>
  signOut: () => Promise<void>
  verifyOTP: (email: string, otp: string, purpose?: string) => Promise<any>
  sendOTP: (email: string) => Promise<void>
  resendOTP: (email: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (email: string, otp: string, newPassword: string, confirmPassword: string) => Promise<void>
  checkEmailExists: (email: string) => Promise<boolean>
  requestEmailVerification: () => Promise<void>
  verifyEmail: (otp: string) => Promise<void>
  setUser: (user: User | null) => void
  refreshUser: () => Promise<User | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

useEffect(() => {
    const initializeAuth = async () => {
      // ✅ Skip auth entirely on landing page
      if (window.location.pathname === '/' || window.location.pathname === '') {
        setUserState(null)
        setIsAuthenticated(false)
        setLoading(false)
        return
      }

      try {
        const token = getStoredToken()
        if (!token) { setLoading(false); return }

        const savedUser = localStorage.getItem('user')
        if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
          try {
            const parsedUser = JSON.parse(savedUser)
            setUserState(parsedUser)
            setIsAuthenticated(true)
            try {
              const currentUser = await api.getCurrentUser()
              setUserState(currentUser)
              localStorage.setItem('user', JSON.stringify(currentUser))
            } catch (error) {
              removeStoredToken()
              localStorage.removeItem('user')
              setUserState(null)
              setIsAuthenticated(false)
            }
          } catch {
            removeStoredToken()
            localStorage.removeItem('user')
            setUserState(null)
            setIsAuthenticated(false)
          }
        }
      } catch {
        setUserState(null)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }
    initializeAuth()
  }, [])




  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getStoredToken()
        if (!token) { setLoading(false); return }

        const savedUser = localStorage.getItem('user')
        if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
          try {
            const parsedUser = JSON.parse(savedUser)
            setUserState(parsedUser)
            setIsAuthenticated(true)
            try {
              const currentUser = await api.getCurrentUser()
              setUserState(currentUser)
              localStorage.setItem('user', JSON.stringify(currentUser))
            } catch (error) {
              removeStoredToken()
              localStorage.removeItem('user')
              setUserState(null)
              setIsAuthenticated(false)
            }
          } catch {
            removeStoredToken()
            localStorage.removeItem('user')
            setUserState(null)
            setIsAuthenticated(false)
          }
        }
      } catch {
        setUserState(null)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }
    initializeAuth()
  }, [])

  const setUser = (newUser: User | null) => {
    setUserState(newUser)
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser))
      setIsAuthenticated(true)
    } else {
      localStorage.removeItem('user')
      setIsAuthenticated(false)
    }
  }

  const refreshUser = async (): Promise<User | null> => {
    try {
      const token = getStoredToken()
      if (!token) { setUserState(null); setIsAuthenticated(false); return null }
      const currentUser = await api.getCurrentUser()
      setUser(currentUser)
      return currentUser
    } catch {
      removeStoredToken()
      localStorage.removeItem('user')
      setUserState(null)
      setIsAuthenticated(false)
      return null
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const response = await api.signIn({ email, password })
      if (response.token) setStoredToken(response.token)
      setUser(response.user)
      toast.success('Welcome back!')

      const inviteToken = localStorage.getItem('inviteToken')
      if (inviteToken) {
        try {
          await fetch(`${API_URL}/team/accept-invite/${inviteToken}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${response.token}` }
          })
          toast.success('🎉 Welcome to the team!')
        } catch {}
        localStorage.removeItem('inviteToken')
      }
    } catch (error) {
      if (error instanceof VerificationRequiredError) {
        const unverifiedError = new Error('Please verify your email first')
        Object.assign(unverifiedError, { code: 'UNVERIFIED_EMAIL', requiresVerification: true, email })
        throw unverifiedError
      }
      toast.error(error instanceof ApiError ? error.message : 'Failed to sign in')
      throw error
    }
  }

  const signUp = async (data: SignUpData) => {
    try {
      const response = await api.signUp(data)
      if (response.token) setStoredToken(response.token)
      setUser(response.user)
      toast.success('Account created!')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Failed to create account')
      throw error
    }
  }

  const signOut = async () => {
    try { await api.signOut() } catch {}
    localStorage.clear()
sessionStorage.clear()
    setUserState(null)
    setIsAuthenticated(false)
    toast.success('Signed out')
    window.location.href = '/'
  }

  const verifyOTP = async (email: string, otp: string, purpose = 'verification'): Promise<any> => {
    try {
      const result = await api.verifyOTP({ email, otp, purpose })
      if (result.success) toast.success('OTP verified!')
      else toast.error('Invalid code')
      return result
    } catch { return { success: false } }
  }

  const sendOTP = async (email: string) => { await api.sendOTP(email); toast.success('Code sent!') }
  const resendOTP = async (email: string) => { await api.sendOTP(email); toast.success('Code resent!') }
  const forgotPassword = async (email: string) => { await api.forgotPassword({ email }); toast.success('Reset code sent!') }
  
  const resetPassword = async (email: string, otp: string, newPassword: string, confirmPassword: string) => {
    await api.resetPassword({ email, otp, newPassword, confirmPassword })
    toast.success('Password reset!')
  }

  const checkEmailExists = async (email: string): Promise<boolean> => {
    try { const r = await api.checkEmail(email); return r.exists || false } catch { return false }
  }

  const requestEmailVerification = async () => { await api.requestEmailVerification(); toast.success('Verification sent!') }
  
  const verifyEmail = async (otp: string) => {
    const result = await api.verifyEmail(otp)
    if (result.emailVerified) { setUser(user ? { ...user, emailVerified: true } : null); toast.success('Email verified!') }
  }

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated, signIn, signUp, signOut,
      verifyOTP, sendOTP, resendOTP, forgotPassword, resetPassword,
      checkEmailExists, requestEmailVerification, verifyEmail, setUser, refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}